import type { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from 'react';
import { cx } from '../cx.js';

/**
 * The one button.
 *
 * Four intents, two tones, and nothing else. The constraint is the feature: an
 * interface with six kinds of button has no primary action, and the teal fill
 * stops meaning "this is the thing to press" the moment a second control
 * borrows it.
 *
 * `tone="dark"` is not a colour scheme, it is a statement about where the
 * button sits. On the violet hero a bordered button needs a light edge; on
 * canvas it needs a dark one. Passing the surface rather than the colours
 * keeps that decision out of every call site.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonTone = 'light' | 'dark';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-control font-medium ' +
  'transition-colors duration-fast ease-out ' +
  'disabled:cursor-not-allowed disabled:opacity-50 ' +
  // A control must stay at least 44px tall where a thumb has to find it.
  // `sm` is for dense desktop tables and is opted into, never defaulted.
  'select-none';

const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-[36px] px-3 text-ui-sm',
  md: 'min-h-[44px] px-4 text-ui',
  lg: 'min-h-[52px] px-6 text-lead',
};

const VARIANTS: Record<ButtonTone, Record<ButtonVariant, string>> = {
  light: {
    // Violet text on teal, not white: white on this teal is 1.9:1 and
    // unreadable. See tokens.test.ts.
    primary: 'bg-teal text-ink hover:bg-teal-strong',
    secondary: 'border border-line-control bg-surface text-ink hover:bg-surface-muted',
    ghost: 'text-ink-muted hover:bg-surface-muted hover:text-ink',
    danger: 'border border-danger bg-danger-wash text-danger hover:bg-danger hover:text-ink-light',
  },
  dark: {
    primary: 'bg-teal text-ink hover:bg-teal-strong',
    secondary: 'border border-line-control-dark bg-transparent text-ink-light hover:bg-violet-rich',
    ghost: 'text-ink-light-muted hover:bg-violet-rich hover:text-ink-light',
    danger: 'border border-danger bg-transparent text-ink-light hover:bg-danger',
  },
};

interface Shared {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly tone?: ButtonTone;
  /** Stretches to the container. Used on mobile, where a half-width CTA reads as unfinished. */
  readonly block?: boolean;
  readonly children: ReactNode;
}

export type ButtonProps = Shared &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>;

export function Button({
  variant = 'primary',
  size = 'md',
  tone = 'light',
  block = false,
  children,
  type = 'button',
  ...rest
}: ButtonProps): React.JSX.Element {
  return (
    <button
      // Defaulted rather than inherited: a <button> inside a <form> submits it
      // unless told otherwise, and that has surprised everyone at least once.
      type={type === 'submit' ? 'submit' : type === 'reset' ? 'reset' : 'button'}
      className={cx(BASE, SIZES[size], VARIANTS[tone][variant], block && 'w-full')}
      {...rest}
    >
      {children}
    </button>
  );
}

export type ButtonLinkProps = Shared &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'>;

/**
 * A link that looks like a button.
 *
 * An anchor, not a button with an onClick that navigates: it has to be
 * openable in a new tab, copyable, and followable without JavaScript. Sign-in
 * depends on this — the identity provider answers with its own page, and a
 * redirect chain fetched by script ends nowhere a person can type a password.
 */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  tone = 'light',
  block = false,
  children,
  ...rest
}: ButtonLinkProps): React.JSX.Element {
  return (
    <a className={cx(BASE, SIZES[size], VARIANTS[tone][variant], block && 'w-full')} {...rest}>
      {children}
    </a>
  );
}
